// Number formatting that follows the SELECTED language.
//
// These used to hardcode nl-NL. The charts were then made locale-aware, which
// left one tooltip rendering "1.250 kg CO2e (12.5%)" in English — a Dutch
// thousands separator beside an English decimal point, in the same sentence.
//
// The language is read from the i18next singleton rather than taken as an
// argument, so the existing call sites (KPI cards, emissions matrix, trips
// table, the LPI module, the Overview) need no change. Every one of them
// already calls useTranslation, so they re-render when the language changes and
// pick up the new convention.
//
// Reading the `i18next` package directly, NOT `i18n/config`: importing the app's
// config would pull i18next initialisation and the translation bundles into
// every module that formats a number, including unit tests. Uninitialised,
// `language` is undefined and intlLocale falls back to Dutch — which is also
// i18next's own fallbackLng, so behaviour outside the app is unchanged.
import i18next from 'i18next';
import { intlLocale } from './localeFormat';

/** Locale for the language in force right now. */
const activeLocale = (): string => intlLocale(i18next.language);

/**
 * Format a number in the active language's convention.
 * @param value - The number to format
 * @param decimals - Number of decimal places (default: 0)
 * @returns Formatted string, or '-' for null/undefined values
 */
export function formatNumber(
  value: number | null | undefined,
  decimals = 0
): string {
  if (value === null || value === undefined) return '-';
  return value.toLocaleString(activeLocale(), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Format a currency value in the active language's convention.
 * @param value - The currency value to format
 * @returns Formatted string with 2 decimal places, or '-' for null values
 */
export function formatCurrency(value: number | null): string {
  if (value === null) return '-';
  return formatNumber(value, 2);
}

/**
 * Format a number with automatic decimal places based on magnitude
 * - >= 1000: 0 decimals
 * - >= 1: 2 decimals
 * - < 1: 3 decimals
 * @param value - The number to format
 * @returns Formatted string, or '-' for null values
 */
export function formatAuto(value: number | null): string {
  if (value === null) return '-';
  if (value >= 1000) return formatNumber(value, 0);
  if (value >= 1) return formatNumber(value, 2);
  return formatNumber(value, 3);
}
